// pages/strategic-playbook/tools/cycle-position.js
// ════════════════════════════════════════════════════
// CYCLE-POSITION INDICATOR (Howard Marks framework)
// ════════════════════════════════════════════════════
// Where are we in the cycle? Defensive at peaks,
// aggressive at bottoms.
// ════════════════════════════════════════════════════

import { useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';

const CREDIT_SPREADS = ['Very Tight', 'Tight', 'Normal', 'Widening', 'Very Wide'];
const SENTIMENT = ['Euphoric', 'Optimistic', 'Mixed', 'Anxious', 'Capitulating'];
const IPO_ACTIVITY = ['Flooding (junk going public)', 'Active', 'Moderate', 'Quiet', 'Frozen'];
const VALUATION = ['Extreme high', 'Stretched', 'Reasonable', 'Cheap', 'Distressed'];
const RISK_APPETITE = ['Risk-blind', 'High', 'Moderate', 'Low', 'Capitulation'];

const PHASE_COLORS = {
  'Early Expansion': 'var(--sp-forest-moss)',
  'Mid Expansion': 'var(--sp-sage-olive)',
  'Late Expansion': 'var(--sp-aged-brass)',
  Peak: 'var(--sp-cognac)',
  'Early Contraction': 'var(--sp-oxblood)',
  'Late Contraction': 'var(--sp-oxblood)',
  Bottom: 'var(--sp-deep-teal)',
};

const STANCE_COLORS = {
  Defensive: 'var(--sp-oxblood)',
  Balanced: 'var(--sp-cognac)',
  Aggressive: 'var(--sp-forest-moss)',
};

export default function CyclePositionTool() {
  const [market, setMarket] = useState('');
  const [creditSpreads, setCreditSpreads] = useState('');
  const [sentiment, setSentiment] = useState('');
  const [ipoActivity, setIpoActivity] = useState('');
  const [valuationLevel, setValuationLevel] = useState('');
  const [riskAppetite, setRiskAppetite] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  async function generate() {
    if (market.trim().length < 3) {
      setError('Specify the market or situation (at least 3 characters).');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await fetch('/api/strategic-playbook/cycle-position', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          market,
          creditSpreads,
          sentiment,
          ipoActivity,
          valuationLevel,
          riskAppetite,
          notes,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to generate');
      }
      setResult(data);
    } catch (err) {
      setError(err.message || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="sp-root">
      <Head>
        <title>Cycle-Position Indicator · Strategic Intelligence</title>
      </Head>

      <div className="sp-page">
        <ToolTopbar />

        <div style={{ marginBottom: 32 }}>
          <Link href="/strategic-playbook/tools" className="sp-reader-back" style={{ marginBottom: 16 }}>← Tools</Link>
          <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.20em', color: 'var(--sp-aged-brass)', marginBottom: 14 }}>
            Strategic Framing · Generator
          </div>
          <h1 style={{ fontFamily: 'var(--sp-font-serif)', fontWeight: 500, fontSize: 32, lineHeight: 1.2, color: 'var(--sp-warm-cream)', margin: '0 0 14px 0', letterSpacing: '-0.01em' }}>
            Cycle-Position Indicator
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.65, color: 'rgba(232, 221, 196, 0.78)', maxWidth: 720, margin: 0 }}>
            Howard Marks framework: <em>cycles dominate.</em> At peaks consensus is bullish
            and the asymmetric move is defensive; at bottoms consensus is bearish and the
            asymmetric move is aggressive. Read 5 indicators, get a phase verdict + stance.
          </p>
        </div>

        {/* Input form */}
        <div style={{ padding: '28px', background: 'var(--sp-soft-charcoal)', border: '1px solid var(--sp-divider)', borderRadius: 'var(--sp-radius-card)', marginBottom: 24 }}>
          <h3 style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', margin: '0 0 22px 0' }}>
            Market + Indicators
          </h3>

          <Field label="Market / situation" hint="Be specific — 'US large-cap tech' or 'private real estate in Tampa' or 'venture capital'">
            <input
              type="text"
              value={market}
              onChange={(e) => setMarket(e.target.value)}
              placeholder="e.g., US large-cap equities"
              style={inputStyle}
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="cycle-grid">
            <SelectField label="Credit spreads" value={creditSpreads} onChange={setCreditSpreads} options={CREDIT_SPREADS} hint="HY/IG bond spreads" />
            <SelectField label="Investor sentiment" value={sentiment} onChange={setSentiment} options={SENTIMENT} hint="VIX, surveys, narrative" />
            <SelectField label="IPO activity" value={ipoActivity} onChange={setIpoActivity} options={IPO_ACTIVITY} hint="Quality + volume" />
            <SelectField label="Valuation level" value={valuationLevel} onChange={setValuationLevel} options={VALUATION} hint="P/E, P/S, vs history" />
            <SelectField label="Risk appetite" value={riskAppetite} onChange={setRiskAppetite} options={RISK_APPETITE} hint="Leverage, lending, behavior" />
          </div>

          <Field label="Additional context (optional)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Any specific data points or recent observations..."
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </Field>

          <div style={{ marginTop: 18, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={generate}
              disabled={loading || market.trim().length < 3}
              style={{
                padding: '12px 24px',
                background: loading || market.trim().length < 3 ? 'transparent' : 'var(--sp-brass-bg)',
                border: `1px solid ${loading || market.trim().length < 3 ? 'var(--sp-divider)' : 'var(--sp-aged-brass)'}`,
                color: loading || market.trim().length < 3 ? 'rgba(232, 221, 196, 0.40)' : 'var(--sp-aged-brass)',
                borderRadius: 'var(--sp-radius-card)',
                fontFamily: 'var(--sp-font-mono)',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.16em',
                cursor: loading || market.trim().length < 3 ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Reading the cycle...' : 'Apply Marks Framework →'}
            </button>
            {error && (
              <div style={{ fontSize: 12, color: 'var(--sp-oxblood)', fontFamily: 'var(--sp-font-mono)' }}>{error}</div>
            )}
          </div>
        </div>

        {loading && (
          <div style={{ padding: '48px', background: 'var(--sp-soft-charcoal)', border: '1px solid var(--sp-divider)', borderRadius: 'var(--sp-radius-card)', textAlign: 'center', fontFamily: 'var(--sp-font-serif)', fontStyle: 'italic', color: 'var(--sp-aged-brass)', fontSize: 16 }}>
            Reading the cycle...
            <div style={{ fontFamily: 'var(--sp-font-mono)', fontStyle: 'normal', fontSize: 11, color: 'rgba(232, 221, 196, 0.50)', marginTop: 8, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
              Marks framework · Phase + Stance
            </div>
          </div>
        )}

        {result && !loading && (
          <div>
            {/* Phase verdict */}
            <div style={{ padding: '32px 36px', background: 'linear-gradient(135deg, var(--sp-card-charcoal) 0%, var(--sp-soft-charcoal) 100%)', border: `1px solid ${PHASE_COLORS[result.phase] || 'var(--sp-aged-brass)'}`, borderRadius: 'var(--sp-radius-card)', marginBottom: 16 }}>
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: PHASE_COLORS[result.phase] || 'var(--sp-aged-brass)', marginBottom: 12 }}>
                Cycle Phase · Confidence: {result.confidence}
              </div>
              <h2 style={{ fontFamily: 'var(--sp-font-serif)', fontWeight: 500, fontSize: 32, lineHeight: 1.15, color: 'var(--sp-warm-cream)', margin: '0 0 14px 0', letterSpacing: '-0.01em' }}>
                {result.phase}
              </h2>
              <div style={{ fontSize: 14, lineHeight: 1.65, color: 'rgba(232, 221, 196, 0.85)', marginBottom: 18 }}>
                {result.phaseDescription}
              </div>
              <div style={{ paddingTop: 16, borderTop: '1px solid var(--sp-divider)' }}>
                <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, color: STANCE_COLORS[result.stance], letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 8 }}>
                  Recommended Stance
                </div>
                <div style={{ fontFamily: 'var(--sp-font-serif)', fontSize: 26, color: STANCE_COLORS[result.stance], marginBottom: 10 }}>
                  {result.stance}
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.6, color: 'rgba(232, 221, 196, 0.80)' }}>
                  {result.stanceRationale}
                </div>
              </div>
            </div>

            {/* Indicators readout */}
            {result.supportingIndicators && result.supportingIndicators.length > 0 && (
              <div style={{ padding: '24px 28px', background: 'var(--sp-soft-charcoal)', border: '1px solid var(--sp-divider)', borderRadius: 'var(--sp-radius-card)', marginBottom: 16 }}>
                <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', marginBottom: 14 }}>
                  Supporting Indicators
                </div>
                {result.supportingIndicators.map((ind, i) => (
                  <div key={i} style={{ padding: '10px 0', borderBottom: i < result.supportingIndicators.length - 1 ? '1px solid rgba(58, 47, 40, 0.5)' : 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontFamily: 'var(--sp-font-serif)', fontSize: 13, color: 'var(--sp-warm-cream)' }}>{ind.name}</span>
                      <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 11, color: 'var(--sp-aged-brass)' }}>{ind.reading}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(232, 221, 196, 0.65)', lineHeight: 1.5 }}>{ind.implication}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Specific moves */}
            <div style={{ padding: '24px 28px', background: 'rgba(58, 84, 71, 0.12)', border: '1px solid rgba(58, 84, 71, 0.40)', borderRadius: 'var(--sp-radius-card)', marginBottom: 16 }}>
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-forest-moss)', marginBottom: 12 }}>
                Specific Moves for This Phase
              </div>
              <ol style={{ margin: 0, paddingLeft: 22 }}>
                {(result.specificMoves || []).map((m, i) => (
                  <li key={i} style={{ fontSize: 14, lineHeight: 1.6, color: 'rgba(232, 221, 196, 0.85)', marginBottom: 8 }}>{m}</li>
                ))}
              </ol>
            </div>

            {/* Watch signals */}
            <div style={{ padding: '24px 28px', background: 'var(--sp-soft-charcoal)', border: '1px solid var(--sp-divider)', borderRadius: 'var(--sp-radius-card)', marginBottom: 16 }}>
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', marginBottom: 12 }}>
                Watch For (signals that would shift the read)
              </div>
              <ul style={{ margin: 0, paddingLeft: 22 }}>
                {(result.watchSignals || []).map((s, i) => (
                  <li key={i} style={{ fontSize: 13, lineHeight: 1.6, color: 'rgba(232, 221, 196, 0.78)', marginBottom: 6 }}>{s}</li>
                ))}
              </ul>
            </div>

            {/* Honest read */}
            <div style={{ padding: '20px 24px', background: 'var(--sp-parchment-bg)', border: '1px solid var(--sp-divider)', borderRadius: 'var(--sp-radius-card)', marginBottom: 16 }}>
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'var(--sp-aged-brass)', marginBottom: 10 }}>
                Honest Read
              </div>
              <div style={{ fontFamily: 'var(--sp-font-serif)', fontStyle: 'italic', fontSize: 14, lineHeight: 1.65, color: 'var(--sp-warm-cream)' }}>
                {result.honestRead}
              </div>
            </div>

            <div style={{ marginTop: 16, fontSize: 12, color: 'rgba(232, 221, 196, 0.55)' }}>
              <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(232, 221, 196, 0.45)', marginRight: 8 }}>
                Connected:
              </span>
              <Link href="/strategic-playbook/marks-second-level-thinking" style={{ color: 'var(--sp-aged-brass)' }}>
                Howard Marks — Second-Level Thinking + Cycle Awareness
              </Link>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @media (max-width: 700px) {
          .cycle-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(232, 221, 196, 0.55)', marginBottom: 6 }}>
        {label}
      </div>
      {children}
      {hint && <div style={{ fontSize: 11, color: 'rgba(232, 221, 196, 0.50)', marginTop: 4, lineHeight: 1.5 }}>{hint}</div>}
    </div>
  );
}

function SelectField({ label, value, onChange, options, hint }) {
  return (
    <Field label={label} hint={hint}>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle}>
        <option value="">— Select —</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </Field>
  );
}

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  background: 'var(--sp-charcoal-black)',
  border: '1px solid var(--sp-divider)',
  borderRadius: 'var(--sp-radius)',
  color: 'var(--sp-warm-cream)',
  fontFamily: 'var(--sp-font-body)',
  fontSize: 14,
  outline: 'none',
};

function ToolTopbar() {
  return (
    <div className="sp-topbar">
      <div>
        <h1>Strategic Intelligence</h1>
        <div className="sp-topbar-meta">Tools · Cycle-Position Indicator</div>
      </div>
      <nav className="sp-topbar-nav">
        <Link href="/strategic-playbook">Dashboard</Link>
        <Link href="/strategic-playbook/library">Library</Link>
        <Link href="/strategic-playbook/tools" className="sp-active">Tools</Link>
        <Link href="/strategic-playbook/decisions">Decisions</Link>
        <Link href="/strategic-playbook/simulate">Simulate</Link>
      </nav>
    </div>
  );
}
